import React, { useEffect, useState } from "react";
import { Checkbox, toast } from "@jammable/ui-core";
import { useFormik } from "formik";
import * as yup from "yup";
import axios from "axios";
import { useAuth } from "../context/AuthProvider";
import { useRouter } from "next/router";
import { loadAuth, loadAuthApi } from "../utils/firebaseAuth";
import {
  customGoogleErrorMessages,
  customSignUpMessages,
} from "../utils/tools";
import Link from "next/link";
import { Spinner } from "@jammable/ui-components/Loader";
import { NextSeo } from "next-seo";
import { useT } from "@jammable/ui-core/src/i18n";
import {
  AuthError,
  AuthLayout,
  AuthTabs,
  GoogleButton,
  OrDivider,
  authErrorText,
  authFieldError,
  authInput,
  authInputError,
  authLabel,
  authLink,
  authPrimary,
} from "../components/auth/JamAuth";

const SignUpPage = ({ clientIp }) => {
  const [error, setError] = useState(null);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user, setUser, updateUser } = useAuth();
  const t = useT();

  // The Firebase SDK loads on demand (utils/firebaseAuth); start now so it
  // is there by the time a button is pressed.
  useEffect(() => {
    loadAuth().catch(() => {});
  }, []);

  const router = useRouter();
  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .trim()
      .email(t("auth.invalidEmail"))
      .required(t("auth.emailRequired")),
    password: yup
      .string()
      .min(6, t("auth.passwordMin"))
      .required(t("auth.passwordRequired")),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!agreeTerms) {
        setError(t("auth.agreeRequired"));
        setLoading(false);
        return;
      }
      setLoading(true);
      loadAuthApi()
        .then(({ auth, fb }) =>
          fb.createUserWithEmailAndPassword(
            auth,
            values.email.trim(),
            values.password,
          ),
        )
        .then((res) => {
          axios
            .post("/api/auth/register", {
              ip: clientIp,
              user: res.user,
            })

            .then(async (res) => {
              updateUser({
                avatar: res.data.user.avatar,
                username: res.data.user.username,
                subscriptions: [],
                credits: 0,
              });
              try {
                if (window.tolt_referral) {
                  window.tolt.signup(res?.data?.user?.email);
                }
              } catch (error) {}
              toast.success(t("auth.welcomeNew"));
              router.push("/");
            })
            .catch(async (err) => {
              if (!err?.response?.data?.isDeleted) {
                await res.user.delete();
              }
              if (err.response.data.message) {
                setError(err.response.data.message);
              } else {
                toast.error(t("auth.registerError"));
              }
            })
            .finally(() => {
              setLoading(false);
            });
        })
        .catch((error) => {
          setError(authErrorText(t, "signUp", customSignUpMessages, error));
          console.error("Error signing in: ", error);
          setLoading(false);
        });
    },
  });

  const handleGoogleSignUp = async () => {
    try {
      const { auth, fb } = await loadAuthApi();
      const result = await fb.signInWithPopup(
        auth,
        new fb.GoogleAuthProvider(),
      );

      const details = fb.getAdditionalUserInfo(result);
      if (!details) {
        await result.user.delete();
        toast.error(t("auth.registerError"));
        throw new Error("Additional user information not available");
      }

      if (details?.isNewUser) {
        axios
          .post("/api/auth/register", {
            user: result?.user,
            ip: clientIp,
          })

          .then(async (res) => {
            updateUser({
              avatar: res?.data?.user?.avatar,
              username: res?.data?.user?.username,
              subscriptions: [],
              credits: 0,
            });
            try {
              if (window.tolt_referral) {
                window.tolt.signup(res?.data?.user?.email);
              }
            } catch (error) {}
            toast.success(t("auth.welcomeNew"));
            router.push("/");
          })
          .catch(async (err) => {
            if (!err?.response?.data?.isDeleted) {
              await result.user.delete();
            }
            if (err.response.data.message) {
              setError(err.response.data.message);
            } else {
              toast.error(t("auth.registerError"));
            }
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        toast.success(t("auth.welcomeBack"));
        router.push("/");
      }
    } catch (error) {
      setError(authErrorText(t, "google", customGoogleErrorMessages, error));
      setLoading(false);
    }
  };

  return (
    <>
      <NextSeo title={t("auth.seo.signupTitle")} noindex={true} />
      <AuthLayout
        heading={
          <>
            {t("auth.signup.heading")}{" "}
            <span className="tw-whitespace-nowrap tw-rounded-[14px] tw-bg-jam-lime tw-px-2 tw-pb-0.5 md:tw-rounded-[18px] md:tw-px-2.5">
              {t("auth.signup.headingHighlight")}
            </span>
          </>
        }
        blurb={t("auth.signup.blurb")}
      >
        <AuthTabs active="signup" />
        <h2 className="jam-display tw-m-0 tw-text-[32px] tw-font-extrabold tw-leading-tight md:tw-text-[40px]">
          {t("auth.createYourAccount")}
        </h2>
        <GoogleButton
          onClick={() => {
            handleGoogleSignUp();
          }}
        >
          {t("auth.continueGoogle")}
        </GoogleButton>
        <OrDivider />
        <form
          onChange={() => {
            setError(null);
          }}
          onSubmit={formik.handleSubmit}
          className="tw-flex tw-w-full tw-flex-col tw-gap-4"
        >
          {error && <AuthError>{error}</AuthError>}
          <label className={authLabel}>
            {t("auth.email")}
            <input
              className={`${authInput} ${formik.touched.email && formik.errors.email ? authInputError : ""}`}
              placeholder={t("auth.emailPlaceholder")}
              inputMode="email"
              autoComplete="email"
              name="email"
              onChange={formik.handleChange}
              value={formik.values.email}
              onBlur={formik.handleBlur}
            />
            {formik.touched.email && formik.errors.email && (
              <span className={authFieldError}>{formik.errors.email}</span>
            )}
          </label>
          <label className={authLabel}>
            {t("auth.password")}
            <input
              className={`${authInput} ${formik.touched.password && formik.errors.password ? authInputError : ""}`}
              placeholder={t("auth.passwordPlaceholderNew")}
              type="password"
              autoComplete="new-password"
              name="password"
              onChange={formik.handleChange}
              value={formik.values.password}
              onBlur={formik.handleBlur}
            />
            {formik.touched.password && formik.errors.password && (
              <span className={authFieldError}>{formik.errors.password}</span>
            )}
          </label>
          <div className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2.5 tw-text-[13px] tw-font-semibold tw-text-jam-muted">
            <Checkbox checked={agreeTerms} onCheckedChange={setAgreeTerms} />
            <div
              onClick={() => {
                setAgreeTerms(!agreeTerms);
              }}
            >
              {t("auth.agreeJammable")}{" "}
              <Link className={authLink} href="/terms">
                {t("auth.terms")}
              </Link>{" "}
              {t("auth.and")}{" "}
              <Link className={authLink} href="/privacy">
                {t("auth.privacy")}
              </Link>
            </div>
          </div>
          <button type="submit" className={authPrimary} disabled={loading}>
            {loading ? (
              <Spinner className="tw-border-white tw-border-3" size={4} />
            ) : (
              t("auth.createAccount")
            )}
          </button>
        </form>
        <div className="tw-text-center tw-text-[13px] tw-font-bold tw-text-jam-muted">
          {t("auth.signup.haveAccount")}{" "}
          <Link className={authLink} href={`/login`}>
            {t("auth.logIn")}
          </Link>
        </div>
      </AuthLayout>
    </>
  );
};

export default SignUpPage;

export async function getServerSideProps(context) {
  const { req } = context;

  const getClientIp = (req) => {
    const ip =
      (req.headers["x-forwarded-for"] || "").split(",").pop().trim() ||
      req.connection.remoteAddress ||
      req.socket.remoteAddress ||
      req.connection.socket.remoteAddress;
    return ip;
  };

  try {
    const clientIp = getClientIp(req);
    return {
      props: {
        clientIp,
      },
    };
  } catch (error) {
    return {
      props: {
        a: "a",
      },
    };
  }
}
